import * as dayjs from 'dayjs';
import { CartItem, CustomerPlacement, ItemOptionType, PreSale } from '../../api';
import { camelize } from 'humps';

interface Params {
  deliveryMethod: string;
  deliveryExpectation: string;
  customerPlacement?: string;
  preSale: PreSale;
}

interface Pattern {
  pattern: RegExp;
  replacement: string;
  shortReplacement?: string;
}

interface DeliveryExpectation {
  deliveryExpectation: string;
  shortDeliveryExpectation: string;
}

const patterns: Record<string, Array<Pattern>> = {
  onDemand: [
    {
      pattern: /Delivery under an hour/gi,
      replacement: 'Arrives in 60 mins',
      shortReplacement: '60 mins',
    },
    {
      pattern: /Delivery in (\d+(?:\.\d+)?-\d+(?:\.\d+)?) minutes/gi,
      replacement: 'Arrives in $1 min',
      shortReplacement: '$1 min',
    },
    {
      pattern: /Delivery in (\d+(?:\.\d+)?-\d+(?:\.\d+)?) hours/gi,
      replacement: 'Arrives in $1 hrs',
      shortReplacement: '$1 hrs',
    },
  ],
  shipped: [
    {
      pattern: /Shipment \(?(\d+(?:-\d+)?)\s?(?:business )?(days)\)?/gi,
      replacement: 'Ships in $1 $2',
    },
    {
      pattern: /Usually ships within (\d+-\d+) business days/gi,
      replacement: 'Ships in $1 days',
    },
  ],
};

export function mapDeliveryExpectation({
  deliveryMethod,
  deliveryExpectation,
  customerPlacement,
  preSale,
}: Params): DeliveryExpectation {
  if (deliveryMethod === 'engraved') {
    const label = 'Ships in 10 days';
    return { shortDeliveryExpectation: label, deliveryExpectation: label };
  }
  if (customerPlacement === 'backOrder') {
    const label = 'On backorder';
    return { shortDeliveryExpectation: label, deliveryExpectation: label };
  }
  if (customerPlacement === 'preSale' && (preSale?.startAt || preSale?.expectedDeliveryDate)) {
    let label = `Available for purchase starting ${parsePreSaleDate(
      preSale?.startAt ?? preSale?.expectedDeliveryDate,
    )}`;
    if (preSale?.purchasable && preSale?.active) {
      label = `Estimated to ship on ${parsePreSaleDate(preSale?.expectedDeliveryDate)}`;
    }
    return { shortDeliveryExpectation: label, deliveryExpectation: label };
  }

  return (patterns[deliveryMethod] || []).reduce(
    (acc, { pattern, replacement, shortReplacement }) => {
      acc.deliveryExpectation = acc.deliveryExpectation.replace(pattern, replacement);
      acc.shortDeliveryExpectation = shortReplacement
        ? acc.shortDeliveryExpectation.replace(pattern, shortReplacement)
        : acc.deliveryExpectation;
      return acc;
    },
    { deliveryExpectation, shortDeliveryExpectation: deliveryExpectation },
  );
}

export function mapDeliveryMethod(cartItem: CartItem) {
  return cartItem.itemOptions?.type === ItemOptionType.Engraving
    ? 'engraved'
    : cartItem.deliveryMethod === 'shipping'
    ? 'shipped'
    : camelize(cartItem.deliveryMethod);
}

function parsePreSaleDate(date: string | Date | number, format = 'MM/DD/YYYY'): string {
  return typeof date === 'number' ? dayjs.unix(date).format(format) : dayjs(date).format(format);
}
